
import Vue from 'vue';
import Prompt from '@/views/Runnits/RunnitSettings/Prompt/Prompt.vue';
import WidthHeightField from '@/views/Runnits/RunnitSettings/WidthHeightField/WidthHeightField.vue';
import ImageInput from '@/views/Runnits/RunnitSettings/ImageInput/ImageInput.vue';
import VideoInput from '@/views/Runnits/RunnitSettings/VideoInput/VideoInput.vue';
import Asset3DInput from '@/views/Runnits/RunnitSettings/Asset3D/Asset3DInput.vue';
import NegativePrompt from '@/views/Runnits/RunnitSettings/NegativePrompt.vue';
import RunnitFieldHeader from '@/views/Runnits/RunnitSettings/RunnitFieldHeader.vue';
import CfgSlider from '@/views/Runnits/RunnitSettings/Sliders/CfgSlider.vue';
import StepsSlider from '@/views/Runnits/RunnitSettings/Sliders/StepsSlider.vue';
import SeedInput from '@/views/Runnits/RunnitSettings/SeedInput.vue';
import RunnitTextField from '@/views/Runnits/RunnitSettings/RunnitTextField.vue';
import RunnitBooleanField from '@/views/Runnits/RunnitSettings/RunnitBooleanField.vue';
import RunnitSingleSelect from '@/views/Runnits/RunnitSettings/SingleSelect/RunnitSingleSelect.vue';
import RunnitBaseSlider from '@/views/Runnits/RunnitSettings/Sliders/RunnitBaseSlider.vue';
import UpscaleFactorSlider from '@/views/Runnits/RunnitSettings/Sliders/UpscaleFactorSlider.vue';
import RunnitModelSelector from '@/views/Runnits/RunnitSettings/RunnitModelSelector.vue';
import FullPageMultiImageSelect from "@/views/Runnits/RunnitSettings/FullPageMultiImageSelect.vue";
import MultiImageInput from "@/views/Runnits/RunnitSettings/ImageInput/MultiImageInput.vue";
import {
	getLogicOnFieldsFromField,
	RunnitNodeField,
	RUNNIT_NODE_FIELD_TYPE,
} from '@run-diffusion/shared';
import ListItem from '@/components/base/ListItem.vue';
import { mapState, mapActions } from 'vuex';
import { Portal } from "portal-vue";
import {
	PORTAL_LOCATION,
} from '@/views/Runnits/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import _isNil from 'lodash/isNil';
import { applyAspectRatioDisabledMessage } from '@/utils/aspectRatio';

export default Vue.extend({
	name: 'RunnitDynamicField',
	props: {
		field: { type: Object, required: true },
		value: { type: [Object, String, Number, Boolean, Array], default: null },
		index: { type: [String, Number], default: null },
		nodeId: { type: String, default: null },
		inputValues: { type: Object, required: true },
	},
	data () {
		return {
			RUNNIT_NODE_FIELD_TYPE,
		};
	},
	computed: {
		featureTourFieldId (): string | null {
			if (!this.field?.__rfield) return null;

			if (this.field.type === RUNNIT_NODE_FIELD_TYPE.PROMPT) return 'board-prompt-field';
			if (this.field.type === RUNNIT_NODE_FIELD_TYPE.IMG) return 'board-image-input-field';
			if (this.field.type === RUNNIT_NODE_FIELD_TYPE.WIDTH_HEIGHT) return 'board-core-settings-field';

			return null;
		},
		...mapState([
			'user',
		]),
		fieldPortalTo () {
			if (this.field.display && this.field.display.mode === 'TRAINER' && this.field.type === RUNNIT_NODE_FIELD_TYPE.IMGS) {
				return PORTAL_LOCATION.TRAINER_IMAGE_UPLOAD;
			}
			return null;
		},
		fieldWrapperIs () {
			return this.fieldPortalTo ? 'portal' : 'div';
		},
		isFirstIndex () {
			return +this.index === 0;
		},
		logicOnFields () {
			const logic = getLogicOnFieldsFromField(this.field, this.inputValues);
			return applyAspectRatioDisabledMessage(logic, this.field, this.inputValues);
		},
		effectiveField () {
			// Overlays logic-driven `required` (RunnitNodeFieldLogicOnFieldRequire) on top
			// of the field's static `required` so leaf components/Vuetify rules see the
			// resolved value without needing to know about the logic block themselves.
			if (!this.field || !this.field.__rfield) return this.field;
			const resolvedRequired: boolean = !!this.field.required || !!this.logicOnFields.triggeredRequires.length;
			if (resolvedRequired === !!this.field.required) return this.field;
			return { ...this.field, required: resolvedRequired };
		},
		isShown () {
			const hasField = !!(this.field && (this.field.__rfield || this.field.__rgroup));
			const showsCondition = !this.logicOnFields.allShows.length || this.logicOnFields.triggeredShows.length;
			const adminCondition = !this.field.adminOnly || (this.user && this.user.isAdmin);

			// Handle autoSet visibility logic
			let autoSetCondition = true;
			// Check if this field has autoSet logic configured
			const hasAutoSetLogic = this.field.logic && this.field.logic.field &&
				Object.values(this.field.logic.field).some((logic: any) => logic.autoSet);

			if (hasAutoSetLogic) {
				// If autoSet logic is configured, respect the visibility setting
				if (this.logicOnFields.autoSetVisible === false) {
					autoSetCondition = false; // Field is hidden when visible: false
				} else {
					autoSetCondition = true; // Field is visible when visible: true or undefined
				}
			}

			return !!(hasField && showsCondition && adminCondition && autoSetCondition);
		},
		isDisabled () {
			const hasRegularDisables = !!(
				this.isShown &&
				this.logicOnFields.triggeredDisables.length
			);

			// Check if autoSet field is visible but not editable
			const hasAutoSetDisable = !!(
				this.logicOnFields.autoSetValue !== undefined &&
				this.logicOnFields.autoSetVisible &&
				!this.logicOnFields.autoSetAllowEdit
			);

			return hasRegularDisables || hasAutoSetDisable;
		},
		isDisabledBooleanFieldDesign () {
			return !!(
				this.isDisabled &&
				this.field.type === RUNNIT_NODE_FIELD_TYPE.BOOLEAN
			);
		},
		hasAutoSetValue () {
			return !!this.logicOnFields.autoSetValue;
		},
		autoSetValue () {
			return this.logicOnFields.autoSetValue;
		},
		hasAutoSetError () {
			return !!this.logicOnFields.autoSetError;
		},
		autoSetError () {
			return this.logicOnFields.autoSetError;
		},
		autoSetAllowEdit () {
			return this.logicOnFields.autoSetAllowEdit;
		},
		targetLargerThanInputError () {
			// Blocked reason for a Non-Upscaling Target. Rendered inline rather than
			// as the disabled-field overlay because the user has to reach the field
			// to fix it, and rather than as a snackbar (like autoSetError) because
			// this is a persistent state, not a transient event.
			if (!this.isShown || this.isDisabled) return null;
			return this.logicOnFields.targetLargerThanInputError || null;
		},
		autoSetVisible () {
			return this.logicOnFields.autoSetVisible;
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		onPromptLoadingChanged (isLoading: boolean) {
			this.$emit('loading-changed', isLoading);
		},
		onFieldInput ({ field, value }: { field: RunnitNodeField, value: any }) {
			this.$emit('input', { field, value });
		},
	},
	watch: {
		autoSetError: {
			handler (newError: string) {
				if (newError) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: newError,
						show: true,
					});
				}
			},
			immediate: true,
		},
	},
	components: {
		FullPageMultiImageSelect,
		MultiImageInput,
		RunnitModelSelector,
		RunnitBaseSlider,
		UpscaleFactorSlider,
		RunnitSingleSelect,
		RunnitBooleanField,
		RunnitTextField,
		SeedInput,
		StepsSlider,
		CfgSlider,
		Prompt,
		ImageInput,
		VideoInput,
		Asset3DInput,
		WidthHeightField,
		NegativePrompt,
		RunnitFieldHeader,
		ListItem,
		Portal,
	},
});
